import { mutate } from 'swr';
import { TypeKeyValueListFields, TypePage } from '@/core/generated-types';
import {
  SubscriptionAddOn,
  SubscriptionAddOnMetadata,
} from '@/hooks/useAddOns';
import { PreviousPlay, StandaloneAddOn } from '@/hooks/useStartingKit';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import interpolate from '@/utils/translations/interpolate';
import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';
import {
  convertIntObj,
  getLocaleInfo,
  orderByScheduleDate,
  toProductVariantGid,
} from '@/utils/helpers';
import { ChildInfo, SubFlowV3ContextData } from '../context/SubFlowV3Context';
import Cookies from 'js-cookie';
import { leiaApiPayload } from '@/utils/leiaClientSide';
import { PaymentOption } from '@/hooks/usePaymentOptions';
import { BundleDiscount } from '@/hooks/useBundleDiscountPreview';
import { SUBSCRIPTION_PRODUCT_ANCHOR } from '@/lib/api/subscriptionManager/types';
import { ReviewStartingKit, UpcomingKit } from '@/hooks/useOrderReview';
import { PaymentOptionExtend } from '@/components/common/types';
import { cart } from '@/lib/cart';
import { addToCart } from '@/lib/cart.browser';
import { formatFlags } from '../../../utils/sessionFlags';
import dayjs from 'dayjs';
import { CartLineInput, ShopifyCart, ShopifyCartItem } from '@/lib/shopify';
import {
  convertCartLineItemPropertiesToAttributes,
  getCartLineItemProperty,
} from '@/utils/shopify';
import { CartItemProperty } from '@/core/constants';
import { normalizeToBoolean } from '@/utils/strings';
import { ROUTES } from '@/app/[locale]/constants';
import { BASE_HEADERS } from '@/lib/api/config';

/**
 * Subscription addon configuration for checkout payload
 */
export interface SubscriptionAddon {
  anchor: string;
  kind: string;
  price: number;
  variantId: string;
}

/**
 * Subscription settings for Play Kit subscriptions in checkout
 */
export interface SubscriptionSettings {
  child_name?: string;
  child_uuid?: string | null;
  date_of_birth?: string | null;
  subscription_anchor?: string;
  starting_kit_anchor?: string;
  startingKitAnchor?: string;
  verifiedDob?: boolean;
  tagline?: string;
  bundle?: {
    uuid?: string;
    size?: string | number;
    price?: string | number;
    savings?: string | number;
    name?: string;
    badge_title?: string | null;
    shopify_variant_id?: string;
    is_default?: boolean | string;
    bundle_title?: string | null;
    kits?: string | null;
  };
  bundleDiscountedPrice?: number;
  bundleAsset?: string;
  bundleVariantId?: string;
  add_ons?: SubscriptionAddon[];
  properties?: {
    item_origin?: string;
  };
  selected_options?: {
    language?: string;
    doll_skin_tone?: string;
  };
  has_opted_in_to_recurring_puf?: boolean | 'true' | 'false';
  starting_product_anchor?: string;
}

/**
 * Gift subscription settings for checkout payload
 */
export interface GiftSettings {
  recipient_first_name?: string;
  recipient_last_name?: string;
  recipient_email_address?: string;
  gift_message?: string;
  gifter_name?: string;
  email_notification_date?: string;
  bundle?: {
    uuid: string;
    size: string | number;
    price: string | number;
    savings: string | number;
    name: string;
    shopify_variant_id: string;
  };
  add_ons?: SubscriptionAddon[];
  properties?: {
    item_origin?: string;
  };
}

/**
 * Subscription plan settings for checkout payload
 */
export interface SubscriptionPlanSettings {
  plan_anchor: string;
  plan_installment_id: string;
  sku: string;
  item_origin?: string;
}

/**
 * Cart data type for the /start_checkout endpoint
 * Extends ShopifyCart with subscription-specific properties
 */
export interface StartCheckoutPayload extends ShopifyCart {
  /** Play Kit subscription configurations */
  subscriptions?: SubscriptionSettings[];
  /** Gift subscription configuration */
  gifts?: GiftSettings;
  /** Course pack or bundle products */
  bundle_products?: ShopifyCartItem[];
  /** User's Lovevery ID */
  user_uuid?: string;
  /** Subscription plan configurations (e.g., Yearly subscription plans) */
  subscription_plans?: SubscriptionPlanSettings[];
  /** Previous play items */
  previous_play?: string[];
}

export const SUBFLOW_SOURCE = 'subflow';
export const SUBFLOW_PRODUCT_ANCHOR = 'subscription-1';
export const SUBFLOW_PRODUCT = { anchor: SUBFLOW_PRODUCT_ANCHOR };
export const USER_DATA_KEY = 'userData';
export const SUBSCRIPTION_GIFTING = 'subscription-gifting';
export const SELECTED_OPTIONS_KEY = 'selected_options';
export const ADD_ONS_KEY = 'add_ons';
export const QR_DATA_KEY = 'qr_data';
export const variantBaseUrl = 'gid://shopify/ProductVariant/';
export const SUB_ASSOCIATIONS =
  'products,products.assets,products.metadata,products.products,products.products.assets,products.products.variants,products.products.variants.assets';
export const SCHEDULED_ITEM_SCHEDULED_TYPES = {
  SCHEDULED: 'scheduled',
  INITIAL_ITEM: 'initial_item',
  PREVIOUS_PLAY: 'previous_play',
};
export const KIT_ASSET_INDICES = {
  OPEN_BOX: 1,
  CLOSED_BOX: 2,
  LIFESTYLE: 3,
  SPLASH_BOX: 4,
  NEW_BOX_WHITE_BG: 5,
  NEW_BOX_GRAY_BG: 6,
  NEW_LIFESTYLE_START: 7,
  NEW_LIFESTYLE_END: 10,
};

export const KIT_ANCHORS = {
  LOOKER: 'kit-the-looker',
  CHARMER: 'kit-the-charmer',
  SENSER: 'kit-the-senser',
  INSPECTOR: 'kit-the-inspector',
  EXPLORER: 'kit-the-explorer',
  THINKER: 'kit-the-thinker',
  BABBLER: 'kit-the-babbler',
  ADVENTURER: 'kit-the-adventurer',
  REALIST: 'kit-the-realist',
  COMPANION: 'kit-the-companion',
  HELPER: 'kit-the-helper',
  ENTHUSIAST: 'kit-the-enthusiast',
  RESEARCHER: 'kit-the-researcher',
  FREE_SPIRIT: 'kit-the-free-spirit',
  OBSERVER: 'kit-the-observer',
  STORYTELLER: 'kit-the-storyteller',
  PROBLEM_SOLVER: 'kit-the-problem-solver',
  ANALYST: 'kit-the-analyst',
  CONNECTOR: 'kit-the-connector',
  EXAMINER: 'kit-the-examiner',
  PERSISTER: 'kit-the-persister',
  PLANNER: 'kit-the-planner',
};

export function toAttributeValue(
  value?: string | number | null,
): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  return String(value);
}

export function normalizeSubscriptionsPayload(payload: unknown): string | null {
  if (payload === undefined || payload === null) {
    return null;
  }
  return typeof payload === 'string' ? payload : JSON.stringify(payload);
}

export const mapTitleToAnchor = (title: string): string | null => {
  const titleMap: { [key: string]: keyof typeof KIT_ANCHORS } = {
    'The Looker Play Kit': 'LOOKER',
    'The Charmer Play Kit': 'CHARMER',
    'The Senser Play Kit': 'SENSER',
    'The Inspector Play Kit': 'INSPECTOR',
    'The Explorer Play Kit': 'EXPLORER',
    'The Thinker Play Kit': 'THINKER',
    'The Babbler Play Kit': 'BABBLER',
    'The Adventurer Play Kit': 'ADVENTURER',
    'The Realist Play Kit': 'REALIST',
    'The Companion Play Kit': 'COMPANION',
    'The Helper Play Kit': 'HELPER',
    'The Enthusiast Play Kit': 'ENTHUSIAST',
    'The Researcher Play Kit': 'RESEARCHER',
    'The Free Spirit Play Kit': 'FREE_SPIRIT',
    'The Observer Play Kit': 'OBSERVER',
    'The Storyteller Play Kit': 'STORYTELLER',
    'The Problem Solver Play Kit': 'PROBLEM_SOLVER',
    'The Analyst Play Kit': 'ANALYST',
    'The Connector Play Kit': 'CONNECTOR',
    'The Examiner Play Kit': 'EXAMINER',
    'The Persister Play Kit': 'PERSISTER',
    'The Planner Play Kit': 'PLANNER',
  };

  const anchorKey = titleMap[title.trim()];
  return anchorKey ? KIT_ANCHORS[anchorKey] : null;
};

export const KIT_ANCHORS_WITH_MONTHS = [
  // Looker kit month range: 0-3 months
  { anchor: KIT_ANCHORS.LOOKER, monthsAgo: 1 },
  // Charmer kit month range: 3-4 months
  { anchor: KIT_ANCHORS.CHARMER, monthsAgo: 3 },
  // Senser kit month range: 5-6 months
  { anchor: KIT_ANCHORS.SENSER, monthsAgo: 5 },
  // Inspector kit month range: 7-8 months
  { anchor: KIT_ANCHORS.INSPECTOR, monthsAgo: 7 },
  // Explorer kit month range: 9-10 months
  { anchor: KIT_ANCHORS.EXPLORER, monthsAgo: 9 },
  // Thinker kit month range: 11-12 months
  { anchor: KIT_ANCHORS.THINKER, monthsAgo: 11 },
  // Babbler kit month range: 13-15 months
  { anchor: KIT_ANCHORS.BABBLER, monthsAgo: 14 },
  // Adventurer kit month range: 16-18 months
  { anchor: KIT_ANCHORS.ADVENTURER, monthsAgo: 17 },
  // Realist kit month range: 19-21 months
  { anchor: KIT_ANCHORS.REALIST, monthsAgo: 20 },
  // Companion kit month range: 22-24 months
  { anchor: KIT_ANCHORS.COMPANION, monthsAgo: 23 },
  // Helper kit month range: 25-27 months
  { anchor: KIT_ANCHORS.HELPER, monthsAgo: 26 },
  // Enthusiast kit month range: 28-30 months
  { anchor: KIT_ANCHORS.ENTHUSIAST, monthsAgo: 29 },
  // Researcher kit month range: 31-33 months
  { anchor: KIT_ANCHORS.RESEARCHER, monthsAgo: 32 },
  // Free Spirit kit month range: 34-36 months
  { anchor: KIT_ANCHORS.FREE_SPIRIT, monthsAgo: 35 },
  // Observer kit month range: 37-39 months
  { anchor: KIT_ANCHORS.OBSERVER, monthsAgo: 38 },
  // Storyteller kit month range: 40-42 months
  { anchor: KIT_ANCHORS.STORYTELLER, monthsAgo: 41 },
  // Problem Solver kit month range: 43-45 months
  { anchor: KIT_ANCHORS.PROBLEM_SOLVER, monthsAgo: 44 },
  // Analyst kit month range: 46-48 months
  { anchor: KIT_ANCHORS.ANALYST, monthsAgo: 47 },
  // Connector kit month range: 49-51 months
  { anchor: KIT_ANCHORS.CONNECTOR, monthsAgo: 50 },
  // Examiner kit month range: 52-54 months
  { anchor: KIT_ANCHORS.EXAMINER, monthsAgo: 53 },
  // Persister kit month range: 55-57 months
  { anchor: KIT_ANCHORS.PERSISTER, monthsAgo: 56 },
  // Planner kit month range: 58-60 months
  { anchor: KIT_ANCHORS.PLANNER, monthsAgo: 59 },
];

export const KIT_ANCHORS_SEQUENCE = [
  KIT_ANCHORS.LOOKER, // : 'kit-the-looker',
  KIT_ANCHORS.CHARMER, // : 'kit-the-charmer',
  KIT_ANCHORS.SENSER, // : 'kit-the-senser',
  KIT_ANCHORS.INSPECTOR, // : 'kit-the-inspector',
  KIT_ANCHORS.EXPLORER, // : 'kit-the-explorer',
  KIT_ANCHORS.THINKER, // : 'kit-the-thinker',
  KIT_ANCHORS.BABBLER, // : 'kit-the-babbler',
  KIT_ANCHORS.ADVENTURER, // : 'kit-the-adventurer',
  KIT_ANCHORS.REALIST, // : 'kit-the-realist',
  KIT_ANCHORS.COMPANION, // : 'kit-the-companion',
  KIT_ANCHORS.HELPER, // : 'kit-the-helper',
  KIT_ANCHORS.ENTHUSIAST, // : 'kit-the-enthusiast',
  KIT_ANCHORS.RESEARCHER, // : 'kit-the-researcher',
  KIT_ANCHORS.FREE_SPIRIT, // : 'kit-the-free-spirit',
  KIT_ANCHORS.OBSERVER, // : 'kit-the-observer',
  KIT_ANCHORS.STORYTELLER, // : 'kit-the-storyteller',
  KIT_ANCHORS.PROBLEM_SOLVER, // : 'kit-the-problem-solver',
  KIT_ANCHORS.ANALYST, // : 'kit-the-analyst',
  KIT_ANCHORS.CONNECTOR, // : 'kit-the-connector',
  KIT_ANCHORS.EXAMINER, // : 'kit-the-examiner',
  KIT_ANCHORS.PERSISTER, // : 'kit-the-persister',
  KIT_ANCHORS.PLANNER, // : 'kit-the-planner',
];

export const STANDALONE_ANCHORS = {
  BLOCK_SET: 'the-block-set',
  STROLLER: 'the-buddy-stroller',
  PLAY_TUNNEL: 'the-play-tunnel',
  PULL_PUP: 'the-pull-pup',
  PLAY_GYM: 'the-play-gym',
  SENSORY_STRANDS: 'sensory-strands',
  ART_CARDS: 'black-and-white-cards',
  NAP_MAT: 'nap-mat',
  SWADDLE_PACK: 'organic-swaddle-packs',
  QUILT: 'organic-baby-quilt',
  FRAMED_PRINTS: 'animal-friends-framed-prints',
  PLACEMAT: 'feeding-bundle',
  CHANGING_PAD: 'organic-changing-pad-covers',
  MONTESSORI_SHELF: 'the-montessori-playshelf',
  STAND_ALONE_GYM: 'stand-alone-gym',
  STAND_ALONE_PULL_PUP: 'stand-alone-pup',
  MUSIC_SET: 'the-music-set',
  FOOD_BEFORE_1_COURSE_PACK: 'the-food-before-1-course-pack',
  POTTY_LEARNING_COURSE_PACK: 'the-potty-learning-course-pack',
  TUMMY_TIME_COURSE_PACK: 'the-tummy-time-course-pack',
};

export const SCHEDULED_ITEM_DELIVERY_STATUSES = {
  NOT_SHIPPED: 'not_shipped',
  WAREHOUSE_NOTIFIED: 'warehouse_notified',
  SHIPPED: 'shipped',
  ERROR: 'error',
  RETURN: 'return',
};
export const SCHEDULED_ITEM_PROCESS_STATUSES = {
  AWAITING_PROCESS: 'awaiting_process',
  SKIP_REQUESTED: 'skip_requested',
  SKIPPED: 'skipped',
  BILLING: 'billing',
  SHIPPING: 'shipping',
  COMPLETED: 'completed',
  ERROR: 'error',
  NA: 'not_applicable',
  CANCELLED: 'cancelled',
  RETURNED: 'returned',
};

export const SKIN_TONE_COLORS = {
  light_skin_tone: 'rgb(239, 222, 209)',
  tan_skin_tone: 'rgb(210, 159, 128)',
  dark_skin_tone: 'rgb(95, 70, 59)',
};

const CUSTOMIZABLE_OPTION_KEYS = {
  LANGUAGE: 'language',
  DOLL_SKIN_TONE: 'doll_skin_tone',
};

export interface SelectOption {
  value?: string;
  label?: string;
  selected?: boolean;
}

export interface FeatureFlags {
  [key: string]: boolean | string | number | null;
}

export const getMonthOptions = (
  t: TypeSubFlowV3Translations,
): SelectOption[] => {
  return [
    { value: '', label: t.month, selected: true },
    { value: '01', label: t.january },
    { value: '02', label: t.february },
    { value: '03', label: t.march },
    { value: '04', label: t.april },
    { value: '05', label: t.may },
    { value: '06', label: t.june },
    { value: '07', label: t.july },
    { value: '08', label: t.august },
    { value: '09', label: t.september },
    { value: '10', label: t.october },
    { value: '11', label: t.november },
    { value: '12', label: t.december },
  ];
};

export const getDayOptions = (
  t: TypeSubFlowV3Translations,
  month?: string,
): SelectOption[] => {
  const year = new Date().getFullYear();
  const targetMonth = month ? Number(month) : new Date().getMonth() + 1;
  const daysInMonth = new Date(year, targetMonth, 0).getDate();

  const days: SelectOption[] = [
    { value: '', label: t.day, selected: true },
    ...Array.from({ length: daysInMonth }, (_, index) => {
      const day = `${index + 1}`;
      return { value: day, label: day };
    }),
  ];

  return days;
};

export const getYearOptions = (
  t: TypeSubFlowV3Translations,
): SelectOption[] => {
  const currentYear = new Date().getFullYear();
  return [
    { value: '', label: t.year, selected: true },
    { value: `${currentYear - 5}`, label: `${currentYear - 5}` },
    { value: `${currentYear - 4}`, label: `${currentYear - 4}` },
    { value: `${currentYear - 3}`, label: `${currentYear - 3}` },
    { value: `${currentYear - 2}`, label: `${currentYear - 2}` },
    { value: `${currentYear - 1}`, label: `${currentYear - 1}` },
    { value: `${currentYear}`, label: `${currentYear}` },
    { value: `${currentYear + 1}`, label: `${currentYear + 1}` },
  ];
};

export const getTranslations = (page: TypePage) => {
  const i18n = page.fields.components?.find(
    (c) => c.sys.contentType.sys.id === 'keyValueList',
  )?.fields as TypeKeyValueListFields;

  let result = {};

  if (i18n) {
    result = generateTranslationsObject(i18n);
  }

  return result as TypeSubFlowV3Translations;
};

export const getAgeBand = (
  meta: SubscriptionAddOnMetadata,
  t: TypeSubFlowV3Translations,
) => {
  const { age_unit: ageUnit, max_age: maxAge, min_age: minAge } = meta;
  const twoMonthProduct = maxAge - minAge === 1;
  const ages = [minAge];

  for (let i = minAge + 1; i < maxAge; i++) {
    ages.push(i);
  }

  ages.push(maxAge);

  if (ageUnit === 'weeks') {
    return interpolate(t.addOnTwoWeeks, {
      minWeek: minAge,
      maxWeek: maxAge,
    });
  }

  if (twoMonthProduct) {
    return interpolate(t.addOnTwoMonths, {
      minMonth: minAge,
      maxMonth: maxAge,
    });
  }

  return interpolate(t.addOnThreeMonths, {
    minMonth: ages[0],
    midMonth: ages[1],
    maxMonth: ages[2],
  });
};

export const getChargeMeta = (
  data: SubFlowV3ContextData,
  t: TypeSubFlowV3Translations,
) => {
  const {
    previousPlayAddOns = [],
    standaloneAddOns = [],
    subscriptionAddOns = [],
    paymentOption,
  } = data;

  if (!paymentOption) {
    return {
      charges: [],
      total: 0,
      subtotal: 0,
    };
  }

  const isPayPerKit = paymentOption.size === 1;

  const subscriptionAddOnsMap = subscriptionAddOns.map((s) => ({
    ...s,
    name: (t as any)[s.kind],
    price: s.metadata.price_cents,
  }));

  const charges = [
    {
      name: `${paymentOption.bundle_title} ${t.susbcriptionPlan}`,
      price: paymentOption.price,
      savings: paymentOption.savings,
    },
    ...previousPlayAddOns.map((p) => ({
      ...p,
      name: interpolate(t.previousPlayAddOn, { kitName: p.name }),
    })),
    ...standaloneAddOns,
    ...(isPayPerKit
      ? subscriptionAddOnsMap.slice(0, 1)
      : subscriptionAddOnsMap),
  ];

  // The existing total was replaced for subtotal because
  // it includes the price with savings included
  const subtotal = charges.reduce((total, current) => {
    if (!isNaN(current.price)) {
      return total + current.price;
    }
    return total;
  }, 0);

  // Total is the sum of all charges including savings
  const total = charges.reduce((total, current) => {
    if (!isNaN(current.price) && 'savings' in current) {
      return total + current.price + current.savings;
    }
    if (!isNaN(current.price)) {
      return total + current.price;
    }
    return total;
  }, 0);

  return { charges, total, subtotal };
};

export const updateAddOns = async (
  oldAddOns: (StandaloneAddOn | PreviousPlay)[] = [],
  newAddOns: (StandaloneAddOn | PreviousPlay)[] = [],
) => {
  const maxLength = Math.max(oldAddOns.length, newAddOns.length);

  try {
    for (let i = 0; i < maxLength; i++) {
      const kitToRemove = oldAddOns?.[i];
      const kitToAdd = newAddOns?.[i];
      const variantIdToRemove = kitToRemove?.variant_id;
      const variantId = kitToAdd?.variant_id;
      if (variantIdToRemove) {
        // await cart.update({ updates: { [variantIdToRemove]: 0 } });
      }
      if (variantId) {
      }
    }
  } catch (e) {
    console.error(e);
  }
};

export const isPlayGymUpsellEligible = (anchor?: string) => {
  if (!anchor) {
    return false;
  }

  const ELIGIBLE_KIT_ANCHORS = [
    KIT_ANCHORS.LOOKER,
    KIT_ANCHORS.CHARMER,
    KIT_ANCHORS.SENSER,
    KIT_ANCHORS.INSPECTOR,
    KIT_ANCHORS.EXPLORER,
  ];

  return ELIGIBLE_KIT_ANCHORS.includes(anchor);
};

export const isYear4KitAnchor = (anchor?: string) => {
  if (!anchor) return false;

  const YEAR_4_ANCHORS = [
    KIT_ANCHORS.CONNECTOR,
    KIT_ANCHORS.EXAMINER,
    KIT_ANCHORS.PERSISTER,
    KIT_ANCHORS.PLANNER,
  ];

  return YEAR_4_ANCHORS.includes(anchor);
};

export const isBookBundleEligible = ({
  productTitle,
  language,
}: {
  productTitle?: string;
  language?: string;
}): boolean => {
  if (!productTitle) return false;

  const anchor = mapTitleToAnchor(productTitle);
  if (!anchor || isYear4KitAnchor(anchor)) return false;
  if (process.env.NEXT_PUBLIC_REGION === 'AU') return false;

  const isEnglish =
    language === 'en' ||
    language === 'en-GB' ||
    language === 'en_GB' ||
    language === undefined;

  return isEnglish;
};

export const SUB_GIFTING = 'subscription-gifting';

export const getBundleAsset = (items: any[]) => {
  const initialItem = items.find(
    (i: any) =>
      i.scheduled_type === SCHEDULED_ITEM_SCHEDULED_TYPES.INITIAL_ITEM,
  );

  const openBoxAsset = initialItem?.product?.assets?.find(
    (a: any) => a.sort_order === KIT_ASSET_INDICES.OPEN_BOX,
  );

  return openBoxAsset?.uri;
};

export const notShipped = (item: any) => {
  return item.delivery_status === SCHEDULED_ITEM_DELIVERY_STATUSES.NOT_SHIPPED;
};

export const isSkipped = (item: any) => {
  return item.process_status === SCHEDULED_ITEM_PROCESS_STATUSES.SKIPPED;
};

export const skipRequested = (item: any) => {
  return item.process_status === SCHEDULED_ITEM_PROCESS_STATUSES.SKIP_REQUESTED;
};

export const isAwaitingProcess = (item: any) => {
  return (
    item.process_status === SCHEDULED_ITEM_PROCESS_STATUSES.AWAITING_PROCESS
  );
};

export const filterPreviousPlayItems = (items: any) => {
  return items.filter(
    (item: any) =>
      item.scheduled_type !== SCHEDULED_ITEM_SCHEDULED_TYPES.PREVIOUS_PLAY,
  );
};

export const filterSkippedItems = (items: any[]) => {
  return items.filter((item) => !isSkipped(item) && !skipRequested(item));
};

export const filterSkippedAwaitingRequestedItems = (items: any[]) => {
  return items.filter((item) => skipRequested(item) || isAwaitingProcess(item));
};

export const getUnshippedItems = (items: any[]) => {
  return items.filter((item) => notShipped(item));
};

// TODO: This function does not return "shipped" items, it returns items that are not "shipped", should we fix it or remove it?
export const getShippedItems = (items: any[]) => {
  return items.filter((item) => !notShipped(item));
};

export const getItemsAwaitingProcess = (items: any[]) => {
  return items.filter((item) => isAwaitingProcess(item));
};

export const getProductForItem = (item: any, products: any[]) => {
  if (!item || !products || products.length === 0) return null;

  return products.find((product) => product.uuid === item.product_uuid);
};

export const getPreviewItems = async (subscription: any, locale?: string) => {
  const defaultOptions = {
    filterPreviousPlays: true,
    filterSkips: false,
    filterPreviewSections: false,
    filterActiveSections: false,
    notShipped: false,
    shipped: false,
    awaitingProcess: false,
    skipRequestedAndAwaitingProcess: false,
  };

  if (!subscription) return [];

  const localeInfo = getLocaleInfo(locale);
  const sectionProducts = await Promise.all(
    subscription.schedule_sections?.map((s: any) => {
      const params = new URLSearchParams({
        anchor: s.subscription_preference.subscription_product_anchor,
        currency_code: localeInfo.currencyCode,
        enable_redux: 'true',
        include_associations: SUB_ASSOCIATIONS,
        // Split language from en-GB to en because this endpoint only accepts this language code
        language: localeInfo.language.split('-')[0],
      });

      return fetch(`/api/v1/product?${params}`).then((r) => r.json());
    }),
  );

  const selectedOptions = { ...defaultOptions };
  const mapProductsToItems = (scheduleSection: any, i: number) => {
    const sectionProduct = sectionProducts[i];
    const subProducts = sectionProduct?.products || [];
    return scheduleSection.scheduled_items.map((item: any) => ({
      ...item,
      product: getProductForItem(item, subProducts),
    }));
  };

  const scheduleSectionsFilterPreview = selectedOptions.filterPreviewSections
    ? subscription.schedule_sections.filter((section: any) => section.uuid)
    : subscription.schedule_sections;
  const scheduledSectionsFilterActive = selectedOptions.filterActiveSections
    ? scheduleSectionsFilterPreview.filter((section: any) => !section.uuid)
    : scheduleSectionsFilterPreview;
  const itemsWithProducts = scheduledSectionsFilterActive.flatMap(
    (s: any, i: number) => mapProductsToItems(s, i),
  );
  const orderedItems = orderByScheduleDate(itemsWithProducts);

  // conditional filtering of items
  const itemsPreviousPlaysFiltered = selectedOptions.filterPreviousPlays
    ? filterPreviousPlayItems(orderedItems)
    : orderedItems;

  const itemsSkipsFiltered = selectedOptions.filterSkips
    ? filterSkippedItems(itemsPreviousPlaysFiltered)
    : itemsPreviousPlaysFiltered;

  const unshippedItemsOnly = selectedOptions.notShipped
    ? getUnshippedItems(itemsSkipsFiltered)
    : itemsSkipsFiltered;

  const shippedItemsOnly = selectedOptions.shipped
    ? getShippedItems(unshippedItemsOnly)
    : unshippedItemsOnly;

  const awaitingProcessItemsOnly = selectedOptions.awaitingProcess
    ? getItemsAwaitingProcess(shippedItemsOnly)
    : shippedItemsOnly;

  const skippedRequestedAwaitingProcess =
    selectedOptions.skipRequestedAndAwaitingProcess
      ? filterSkippedAwaitingRequestedItems(awaitingProcessItemsOnly)
      : awaitingProcessItemsOnly;

  return skippedRequestedAwaitingProcess.map((item: any) => ({
    ...item,
    subscription_source: subscription.subscription_source,
    gifter_billing_first_name: subscription.billing_address?.first_name,
  }));
};

export const getGiftItem = (cartData: StartCheckoutPayload) => {
  return cartData.items?.find((i) => i.handle === SUB_GIFTING);
};

export const addGiftItems = (giftItem: any, cartData: StartCheckoutPayload) => {
  const giftCartData =
    giftItem.properties?.gifts || giftItem.properties?._gifts;
  const giftAddOns = giftCartData?.add_ons;
  const addOns: SubscriptionAddon[] = [];

  if (giftAddOns?.length) {
    addOns.push(
      ...(Object.values(giftAddOns).map((addon) =>
        convertIntObj(addon as any),
      ) as unknown as SubscriptionAddon[]),
    );
  }

  cartData.items = [{ handle: SUBSCRIPTION_GIFTING }] as ShopifyCartItem[];
  cartData.gifts = {
    ...(giftCartData as GiftSettings),
    add_ons: addOns,
    bundle: giftCartData?.bundle
      ? (convertIntObj(giftCartData.bundle, [
          'shopify_variant_id',
          'uuid',
        ]) as GiftSettings['bundle'])
      : undefined,
  };

  return cartData;
};

export const getBirthDate = (childInfo: Partial<ChildInfo>) => {
  if (!childInfo?.day || !childInfo?.month || !childInfo.year) return '';
  return `${childInfo.year}-${childInfo.month}-${childInfo.day}`;
};

export const findCustomizationId = (kits: any[]) => {
  if (!kits || kits.length === 0) {
    return null;
  }
  return kits.find((kit) => !!kit.customizationId === true)?.customizationId;
};

export const getSelectedOptions = (data: SubFlowV3ContextData) => {
  const options: Record<string, string> = {};
  const selectedLanguage = data.language;
  const selectedSkinTone =
    data.customizationId ||
    findCustomizationId(data.previousPlayAddOns) ||
    findCustomizationId(data.standaloneAddOns);

  if (!selectedLanguage && !selectedSkinTone) {
    return null;
  }

  if (selectedLanguage) {
    options[CUSTOMIZABLE_OPTION_KEYS.LANGUAGE] = selectedLanguage;
  }

  if (selectedSkinTone) {
    options[CUSTOMIZABLE_OPTION_KEYS.DOLL_SKIN_TONE] = selectedSkinTone;
  }

  return options;
};

export const addSubscriptionAddOns = (
  cartData: StartCheckoutPayload,
  subflowData: SubFlowV3ContextData['subscriptionAddOns'],
  paymentOptionData: SubFlowV3ContextData['paymentOption'],
) => {
  if (cartData.subscriptions?.[0]) {
    cartData.subscriptions[0].add_ons = subflowData.map((s) => ({
      anchor: s.anchor,
      kind: s.kind === 'books_addon' ? 'books' : s.kind,
      price: s.metadata.price_cents * (paymentOptionData?.size ?? 0),
      variantId: s.variants?.[0]?.external_id ?? '',
    }));
  }

  return cartData;
};

export const extractItems = (
  cartItems: any,
  subflowData: SubFlowV3ContextData,
) => {
  const previousPlays =
    subflowData.previousPlayAddOns?.map((addon) => addon.anchor) || [];

  const subscriptionItems = cartItems.filter(
    (i: any) => i.handle === SUBSCRIPTION_PRODUCT_ANCHOR.LOVEVERY_SUBSCRIPTION,
  );
  const subscriptionPlanItems = cartItems.filter(_findSubscriptionPlanProducts);

  const filteredItems = cartItems
    .filter((i: any) => !i.handle.includes('the-play-kits-'))
    .filter((i: any) => i.handle !== SUB_GIFTING)
    .filter(_filterSubscriptionPlanProducts)
    .filter(_filterBookBundleAddonProducts);

  return {
    items: filteredItems,
    subscriptionItems,
    previousPlays,
    subscriptionPlanItems,
  };
};

export const isCoursePack = (item: any) => {
  return (
    item?.properties?.is_course_pack === 'true' ||
    item?.properties?._is_course_pack === 'true'
  );
};

export function extractCoursePackItems(cartItems: any[]) {
  const coursePackItems: any[] = [];
  const allOtherItems: any[] = [];

  cartItems.forEach((item) => {
    if (isCoursePack(item)) {
      coursePackItems.push(item);
    } else {
      allOtherItems.push(item);
    }
  });

  return {
    coursePackItems,
    allOtherItems,
  };
}

export const hasCoursePackItems = (cartItems?: any[]) => {
  return cartItems?.some(isCoursePack) ?? false;
};

export const addCoursePackItems = (cartData: StartCheckoutPayload) => {
  const { coursePackItems, allOtherItems } = extractCoursePackItems(
    cartData.items,
  );

  cartData.bundle_products = coursePackItems;
  cartData.items = allOtherItems;
};

const addPreviousPlaysToCart = async (
  cartPreviousPlays: any,
  previousPlayAddOns: PreviousPlay[],
) => {
  try {
    // remove any existing prev plays from cart before adding || updating prev play selection
    if (cartPreviousPlays.length > 0) {
      const formData = {
        updates: {},
      };
      for (const previousPlay in cartPreviousPlays) {
        const kit = cartPreviousPlays[previousPlay];
        const updates: Record<number, any> = formData['updates'];
        updates[parseInt(kit.variant_id)] = 0;
      }
      // await cart.update(formData);
    }

    if (previousPlayAddOns.length > 0) {
      // const previousPlays = previousPlayAddOns.map((kit) => {
      //   return { id: kit.variant_id };
      // });
    }
  } catch (error) {
    console.error(error);
  }
};

export async function finalizeCart(
  cartData: StartCheckoutPayload,
  subflowData: SubFlowV3ContextData,
  subscriptionsPayload: SubscriptionSettings,
  originalItems: ShopifyCartItem[],
): Promise<ShopifyCart | undefined> {
  const previousPlayAddOns = subflowData.previousPlayAddOns;
  const kitBundle = cartData.items.filter((item) =>
    item.handle?.includes(SUBFLOW_PRODUCT.anchor),
  )[0];
  const BUNDLE_VARIANT_ID = subscriptionsPayload.bundleVariantId ?? '';
  const addOns = cartData.subscriptions?.[0]?.add_ons ?? [];

  // Step 1: Collect all items to remove in one batch (bundle + existing addOns)
  const itemsToRemove: string[] = [];

  if (kitBundle && BUNDLE_VARIANT_ID) {
    itemsToRemove.push(BUNDLE_VARIANT_ID);
  }

  const existingAddOns = addOns
    .filter(({ anchor }) => originalItems.find((i) => i.handle === anchor))
    .map(({ variantId }) => variantId);

  itemsToRemove.push(...existingAddOns);

  // Step 2: Single batch remove for all items (bundle + addOns)
  if (itemsToRemove.length > 0) {
    await cart.batchRemove(itemsToRemove);
  }

  // Step 3: Collect all items to add (bundle + addOns) using Storefront API format
  const bundleAttributes = convertCartLineItemPropertiesToAttributes({
    is_playgenius_subscription: 'true',
    [CartItemProperty.PlayKitSubscriptionSettings]:
      normalizeSubscriptionsPayload(subscriptionsPayload),
  });

  const itemsToAdd: CartLineInput[] = [
    {
      merchandiseId: toProductVariantGid(BUNDLE_VARIANT_ID),
      quantity: 1,
      attributes: bundleAttributes,
    },
  ];

  if (addOns.length > 0) {
    const addOnItems = addOns.map(
      ({ variantId, price, anchor }: any): CartLineInput => ({
        merchandiseId: toProductVariantGid(variantId),
        quantity: 1,
        attributes: convertCartLineItemPropertiesToAttributes({
          price: toAttributeValue(price),
          handle: toAttributeValue(anchor),
          id: toAttributeValue(variantId),
          [CartItemProperty.IsPlayKitSubscriptionBookBundleAddon]: 'true',
          type: 'Subscription Add-On',
          vendor: 'Lovevery',
          [CartItemProperty.LinkedProductHandle]: SUBFLOW_PRODUCT_ANCHOR,
        }),
      }),
    );
    itemsToAdd.push(...addOnItems);
  }

  // Step 4: Single batch add for all items using Cart Service API
  await addToCart(itemsToAdd, 1, { noredirect: 1 });

  const cartPreviousPlays = originalItems.filter((item: any) =>
    item.handle.includes('the-play-kits-'),
  );
  await addPreviousPlaysToCart(cartPreviousPlays, previousPlayAddOns);

  // Return cart data for analytics - fetch fresh cart for consistent format
  return cart.get();
}

export function getShopifyStorefrontDigestCookie() {
  const cookies = document.cookie.split(';');
  const [shopifyCookie] = cookies.filter(
    (cookie) => !!cookie.includes('storefront_digest'),
  );
  return shopifyCookie ? shopifyCookie.split('=')[1] : null;
}

export async function startCheckout(
  cartData: StartCheckoutPayload,
  flags: FeatureFlags,
  userJwt?: string,
) {
  const cookie = getShopifyStorefrontDigestCookie();

  const url = `/api/v1/checkout_orchestration/start_checkout?source=checkout_orchestration&storefront_digest_cookie=${cookie}`;

  const options = {
    method: 'POST',
    body: JSON.stringify({
      ...leiaApiPayload(),
      ...cartData,
      le_flags: formatFlags(flags),
    }),
    headers: {
      ...BASE_HEADERS,
      ...(userJwt && { Authorization: userJwt }),
      'X-User-Agent': navigator.userAgent,
    },
  };

  const response = await fetch(url, options);

  return response.json();
}
export async function redirectToCart() {
  await mutate('/cart');
  window.location.href = '/cart';
}

export async function redirectToPlayGeniusCheckout(
  invoiceId: string,
  isRecurringPuf: boolean,
  localeInfo: ReturnType<typeof getLocaleInfo>,
  discountCode?: string,
  referrer?: string,
) {
  const loveveryId = localStorage.getItem('lovevery_id');

  // Localization settings
  const queryParams = [
    `language=${localeInfo.language}`,
    `currency=${localeInfo.currencyCode.toLowerCase()}`,
    `epb=val`,
    `esc=val`,
    `referrer=${referrer || 'subflow'}`,
  ];

  if (discountCode) {
    queryParams.push(`referral_code=${discountCode}`);
  }

  if (loveveryId) {
    queryParams.push(`lovevery_id=${loveveryId}`);
  }

  if (isRecurringPuf) {
    queryParams.push(`has_opted_in_to_recurring_puf=true`);
  }

  Cookies.remove('referral_code');
  window.localStorage.removeItem('subflow-v3-referral-code');

  const url = `/${localeInfo.locale}${ROUTES.CHECKOUT_CUSTOMER_INFORMATION}/${invoiceId}?${queryParams.join('&')}`;

  window.location.href = url; // Force a full reload, to avoid a blank page.
}

export const getBookMedia = (addOns: SubscriptionAddOn[]) => {
  return addOns.reduce<any>((allBundles, currentBundle) => {
    return [
      ...allBundles,
      ...currentBundle.components.reduce<any>(
        (allComponents, currentComponent) => {
          return [
            ...allComponents,
            ...currentComponent.media
              .filter((m) => m.key === 'gallery')
              .map((m) => ({
                ...m,
                bookTitle: currentComponent.copy_text.title,
                bundleTitle: currentBundle.copy_text.title,
              })),
          ];
        },
        [],
      ),
    ];
  }, []);
};

export const getUpsellAddOns = (
  startingKitAnchor: string,
  locale = 'en-US',
) => {
  const isEuUkStore = ['fr', 'de', 'nl', 'es', 'en-GB', 'en-EU'].includes(
    locale,
  );

  const BABY_ADD_ONS_ANCHORS_US = ['the-play-gym', 'sensory-strands'];
  const BABY_TODDLER_CUSP_ADD_ONS_ANCHORS_US = [
    'the-play-tunnel',
    'the-block-set',
  ];
  const TODDLER_ADD_ONS_ANCHORS_US = ['the-block-set', 'the-play-tunnel'];
  const BABY_ADD_ONS_ANCHORS_EU = ['the-play-gym'];
  const TODDLER_ADD_ONS_ANCHORS_EU = ['the-block-set'];

  const { LOOKER, CHARMER, SENSER, INSPECTOR, EXPLORER, THINKER, BABBLER } =
    KIT_ANCHORS;
  const BABY_KIT_ANCHORS = [LOOKER, CHARMER, SENSER, INSPECTOR, EXPLORER];
  const BABY_TODDLER_CUSP_KIT_ANCHORS = [THINKER, BABBLER];

  if (BABY_KIT_ANCHORS.includes(startingKitAnchor)) {
    return isEuUkStore ? BABY_ADD_ONS_ANCHORS_EU : BABY_ADD_ONS_ANCHORS_US;
  }

  if (BABY_TODDLER_CUSP_KIT_ANCHORS.includes(startingKitAnchor)) {
    return isEuUkStore
      ? TODDLER_ADD_ONS_ANCHORS_EU
      : BABY_TODDLER_CUSP_ADD_ONS_ANCHORS_US;
  }

  return isEuUkStore ? TODDLER_ADD_ONS_ANCHORS_EU : TODDLER_ADD_ONS_ANCHORS_US;
};

export const isUSStore = (locale: string) => {
  return locale === 'en-US';
};

export const isEUStore = () => {
  const region = process.env.NEXT_PUBLIC_REGION;
  const isEUStore = region === 'EU';
  return isEUStore;
};

export const getBundleDiscountValue = (
  selectedOption: PaymentOption | PaymentOptionExtend | null,
  bundleDiscountPreview: BundleDiscount[] | undefined,
  withAddons: boolean,
) => {
  // This is used as a fallback to the default state when
  // the bundle discount endpoint has not been requested
  if (!bundleDiscountPreview) return 0;

  const bundleDiscount = bundleDiscountPreview.find(
    (bundle) => bundle.uuid === selectedOption?.uuid,
  );

  if (!bundleDiscount) return 0;

  return withAddons
    ? bundleDiscount.with_addons.discount_value
    : bundleDiscount.discount_value;
};

export const applyBundleDiscountToOrder = (
  orders: (StandaloneAddOn | ReviewStartingKit | UpcomingKit)[],
  bundleDiscountValue: number,
) => {
  if (!orders.length || !bundleDiscountValue) return orders;

  const bundleDiscountPercentage = bundleDiscountValue / 100;

  return orders.map((order) => {
    // Add bundle discount to add-ons
    if ('add_ons' in order) {
      return {
        ...order,
        discounted_price: order.price - order.price * bundleDiscountPercentage,
        add_ons: order.add_ons.map((addOn) => ({
          ...addOn,
          metadata: {
            ...addOn.metadata,
            discounted_price:
              addOn.metadata?.price_cents -
              addOn.metadata?.price_cents * bundleDiscountPercentage,
          },
        })),
      };
    }
    return {
      ...order,
      discounted_price: order.price - order.price * bundleDiscountPercentage,
    };
  });
};

export function getDOBByAnchor(anchor: string): string | null {
  const kit = KIT_ANCHORS_WITH_MONTHS.find((kit) => kit.anchor === anchor);
  if (!kit) {
    return null;
  }

  if (anchor === KIT_ANCHORS.LOOKER) {
    return dayjs().format('YYYY-MM-DD');
  }

  const dob = dayjs().subtract(kit.monthsAgo, 'month');
  return dob.format('YYYY-MM-DD');
}

export const KIT_ANCHOR_AGE_RANGES: Record<string, string> = {
  [KIT_ANCHORS.LOOKER]: '0-3',
  [KIT_ANCHORS.CHARMER]: '3-4',
  [KIT_ANCHORS.SENSER]: '5-6',
  [KIT_ANCHORS.INSPECTOR]: '7-8',
  [KIT_ANCHORS.EXPLORER]: '9-10',
  [KIT_ANCHORS.THINKER]: '11-12',
  [KIT_ANCHORS.BABBLER]: '13-15',
  [KIT_ANCHORS.ADVENTURER]: '16-18',
  [KIT_ANCHORS.REALIST]: '19-21',
  [KIT_ANCHORS.COMPANION]: '22-24',
  [KIT_ANCHORS.HELPER]: '25-27',
  [KIT_ANCHORS.ENTHUSIAST]: '28-30',
  [KIT_ANCHORS.RESEARCHER]: '31-33',
  [KIT_ANCHORS.FREE_SPIRIT]: '34-36',
  [KIT_ANCHORS.OBSERVER]: '37-39',
  [KIT_ANCHORS.STORYTELLER]: '40-42',
  [KIT_ANCHORS.PROBLEM_SOLVER]: '43-45',
  [KIT_ANCHORS.ANALYST]: '46-48',
  [KIT_ANCHORS.CONNECTOR]: '49-51',
  [KIT_ANCHORS.EXAMINER]: '52-54',
  [KIT_ANCHORS.PERSISTER]: '55-57',
  [KIT_ANCHORS.PLANNER]: '58-60',
};

export function getAgeRangeByAnchor(anchor: string): string | null {
  return KIT_ANCHOR_AGE_RANGES[anchor] ?? null;
}

export function _getItemAttribute(
  item: ShopifyCartItem,
  property: CartItemProperty,
) {
  const attributes = convertCartLineItemPropertiesToAttributes(item.properties);

  return getCartLineItemProperty(property, attributes);
}

export function _findSubscriptionPlanProducts(item: ShopifyCartItem) {
  return _getItemAttribute(item, CartItemProperty.SubscriptionPlanSettings);
}

export function _filterSubscriptionPlanProducts(item: ShopifyCartItem) {
  return !_getItemAttribute(item, CartItemProperty.SubscriptionPlanSettings);
}

export function _filterBookBundleAddonProducts(item: ShopifyCartItem) {
  return !normalizeToBoolean(
    _getItemAttribute(
      item,
      CartItemProperty.IsPlayKitSubscriptionBookBundleAddon,
    ),
  );
}

export const normalize = (str: string): string =>
  str.toLowerCase().replace(/[_\-]/g, ' ').split(' ').sort().join('');

export const findVariantByKey = (variants: any[], key: string) =>
  variants.find((variant) => normalize(variant.title) === normalize(key));

export const formatCentsToDollars = (cents?: number): string | number => {
  if (!cents) return 0;
  return (cents / 100).toFixed(2);
};

/**
 * Maps language codes to variant titles for EU store
 * @param languageCode - Language code like 'en', 'fr', 'es', etc.
 * @returns Variant title like 'British English', 'French', 'Spanish', etc.
 */
export const mapLanguageCodeToVariantTitle = (languageCode: string): string => {
  const LANGUAGE_TO_VARIANT_MAPPING: Record<string, string> = {
    en: 'British English',
    'en-GB': 'British English',
    'en-EU': 'British English',
    'en-AU': 'British English',
    'en-US': 'British English',
    nl: 'Dutch',
    de: 'German',
    es: 'Spanish',
    fr: 'French',
  };

  return LANGUAGE_TO_VARIANT_MAPPING[languageCode] || languageCode;
};

export const findEUVariantByCustomizationAndLanguage = (
  variants: any[],
  customizationId: string,
  language: string,
) => {
  let skinToneTitle = '';

  if (customizationId.includes('light')) {
    skinToneTitle = 'Light Skin Tone';
  } else if (customizationId.includes('dark')) {
    skinToneTitle = 'Dark Skin Tone';
  } else if (customizationId.includes('tan')) {
    skinToneTitle = 'Tan Skin Tone';
  }
  const languageTitle = mapLanguageCodeToVariantTitle(language);
  // Create the expected format: "Dark Skin Tone / British English"
  const expectedVariantTitle = `${skinToneTitle} / ${languageTitle}`;

  return variants.find(
    (variant) => normalize(variant.title) === normalize(expectedVariantTitle),
  );
};

export const findVariantByCustomizationData = (
  variants: any[],
  customizationId: string,
  language?: string,
  isEU?: boolean,
) => {
  // For EU markets, use combined skin tone + language matching
  if (isEU && language && customizationId) {
    return findEUVariantByCustomizationAndLanguage(
      variants,
      customizationId,
      language,
    );
  }

  // For other markets, use the original skin tone only matching
  return findVariantByKey(variants, customizationId);
};
